import { useEffect, useRef } from 'react'
import { INGLIS_BLUE } from '../../constants/colors'
import type { InstallGuide as Guide } from '../../hooks/usePwaInstall'

/** iOS/macOS share glyph: a box with an arrow leaving the top. */
const ShareGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
    strokeLinecap="round" strokeLinejoin="round" width="22" height="22" aria-hidden="true">
    <path d="M12 15V3m0 0L8 7m4-4 4 4" />
    <path d="M6 12H5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-1" />
  </svg>
)

/** Android/Chrome overflow menu glyph. */
const MenuGlyph = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true">
    <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
  </svg>
)

/** The "Add to Home Screen" row icon — a square with a plus, as the OS menus draw it. */
const AddGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
    strokeLinecap="round" strokeLinejoin="round" width="18" height="18" aria-hidden="true">
    <rect x="3.5" y="3.5" width="17" height="17" rx="4" /><path d="M12 8.5v7M8.5 12h7" />
  </svg>
)

/**
 * Full-screen guided overlay for browsers with no install API.
 *
 * Safari and Firefox cannot be driven into an install from script — the menu holding "Add to Home
 * Screen" is browser chrome. Rather than print a paragraph and hope, this points at the physical
 * control: the glyph to look for, an arrow aimed at the screen edge it sits on, and the menu row
 * drawn the way it actually appears.
 */
export default function InstallGuide({ title, guide, onClose, onDownloadShortcut, shortcutMsg, shortcutLabel }: {
  title: string
  guide: Guide
  onClose: () => void
  /** Optional secondary action: write a shortcut file the user can drag to the Dock/Desktop. */
  onDownloadShortcut?: () => void
  shortcutMsg?: string
  shortcutLabel?: string
}) {
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const pointsDown = guide.location === 'bottom-bar'
  const ControlGlyph = guide.control === 'share' ? ShareGlyph : MenuGlyph
  const controlName = guide.control === 'share' ? 'Share' : 'Menu'

  return (
    <div
      className="print:hidden"
      style={{
        position: 'fixed', inset: 0, zIndex: 70,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        // Keep the card clear of the very toolbar we're pointing at.
        paddingBottom: pointsDown ? 132 : 16,
        paddingTop: pointsDown ? 16 : 92,
      }}
    >
      {/* A real <button> rather than a div with onClick: the backdrop is a dismiss control, so it
          has to be reachable and operable by keyboard like any other. The dialog sits above it in
          the stacking order, so clicks inside the card never reach it — no event-bubbling hacks. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          border: 'none', padding: 0,
          background: 'rgba(0,0,0,0.55)',
          cursor: 'pointer',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-guide-title"
        style={{
          position: 'relative', zIndex: 1,
          width: '100%', maxWidth: 340,
          background: '#fff', color: '#1a1a1a',
          borderRadius: 14, padding: '20px 20px 16px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          fontSize: '0.92rem', lineHeight: 1.5,
        }}
      >
        <h2 id="install-guide-title" style={{ margin: '0 0 14px', fontSize: '1.05rem', fontWeight: 800 }}>
          {title}
        </h2>

        <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 14 }}>
          <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Step n={1} />
            <span>
              {guide.verb} the <strong>{controlName}</strong> button
              <span style={{
                display: 'inline-flex', verticalAlign: 'middle', alignItems: 'center',
                margin: '0 5px', padding: '3px 6px',
                border: '1px solid #c8ccd2', borderRadius: 7, color: INGLIS_BLUE, background: '#f6f8fa',
              }}>
                <ControlGlyph />
              </span>{' '}
              {guide.where}.
            </span>
          </li>

          <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Step n={2} />
            <span style={{ minWidth: 0 }}>
              {guide.verb === 'Click' ? 'Then choose this from the list:' : 'Then choose this from the list:'}
              {/* Drawn as the OS renders the row, so it's recognisable at a glance. */}
              <span style={{
                display: 'flex', alignItems: 'center', gap: 8,
                marginTop: 8, padding: '9px 11px',
                border: '1px solid #c8ccd2', borderRadius: 9, background: '#f6f8fa',
                fontWeight: 700, color: '#1a1a1a',
              }}>
                <AddGlyph />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {guide.menuItem}
                </span>
              </span>
            </span>
          </li>
        </ol>

        {/* Arrow aimed at the toolbar the control actually lives on. Coloured, not white: it sits
            on the white card, so the original #fff rendered it invisible. */}
        <div aria-hidden="true" style={{
          display: 'flex', justifyContent: pointsDown ? 'center' : 'flex-end',
          marginTop: 12, color: INGLIS_BLUE,
        }}>
          <span style={{ fontSize: '1.7rem', lineHeight: 1, fontWeight: 700 }}>
            {pointsDown ? '↓' : '↑'}
          </span>
        </div>

        {/* Secondary: the install above is the better outcome (a real standalone app), but this
            is the one thing the page can actually perform itself rather than describe. */}
        {onDownloadShortcut && (
          <>
            <div style={{ borderTop: '1px solid #e6e9ed', margin: '14px 0 12px' }} />
            <button
              type="button"
              onClick={onDownloadShortcut}
              style={{
                width: '100%', height: 38,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                border: `1px solid ${INGLIS_BLUE}`, borderRadius: 8,
                background: '#fff', color: INGLIS_BLUE,
                fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
              }}
            >
              {shortcutLabel ?? 'Add shortcut to my computer'}
            </button>
            <p role="status" style={{ margin: '8px 0 0', minHeight: '1.2em', fontSize: '0.82rem', color: '#3a6b35' }}>
              {shortcutMsg}
            </p>
          </>
        )}

        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          style={{
            marginTop: 10, width: '100%', height: 40,
            border: 'none', borderRadius: 8,
            background: INGLIS_BLUE, color: '#fff',
            fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
          }}
        >
          Got it
        </button>
      </div>
    </div>
  )
}

function Step({ n }: { n: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 23, height: 23, borderRadius: '50%',
        background: INGLIS_BLUE, color: '#fff',
        fontSize: '0.78rem', fontWeight: 800,
      }}
    >
      {n}
    </span>
  )
}
